import dayjs from 'dayjs';
import store from '../../Redux/Store';

// Import necessary localization files
import 'dayjs/locale/en-ca';
import 'dayjs/locale/en-au';
import 'dayjs/locale/en-nz';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';

const advancedFormat = require('dayjs/plugin/advancedFormat');
const localizedFormat = require('dayjs/plugin/localizedFormat');
const customParseFormat = require('dayjs/plugin/customParseFormat');
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');
const localizationConstants = require('../Constants/LocalizationConstants');

dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export function setupDateLocalization() {
    const localStore = store.getState();
    const { country } = localStore.prefillReducer;

    if (country === localizationConstants.AU) {
        dayjs.locale('en-au');
    } else if (country === localizationConstants.NZ) {
        dayjs.locale('en-nz');
    } else if (country === localizationConstants.CA) {
        if (localStore.prefillReducer.locale.toLowerCase() === 'fr-ca') {
            dayjs.locale('fr-ca');
        } else {
            dayjs.locale('en-ca');
        }
    } else if (country === localizationConstants.BR) {
        dayjs.locale('pt-br');
    } else if (country === localizationConstants.US) {
        dayjs.locale('en');
    } else if (
        country === localizationConstants.AR ||
        country === localizationConstants.CL ||
        country === localizationConstants.CO ||
        country === localizationConstants.EC ||
        country === localizationConstants.MX ||
        country === localizationConstants.PE
    ) {
        dayjs.locale('es');
    } else {
        dayjs.locale('en');
    }
}

export function formatDateLong(date) {
    const localStore = store.getState();
    const { country } = localStore.prefillReducer;
    let formattedDate = '';

    if (country === localizationConstants.US) {
        formattedDate = dayjs(date).format('dddd, MMMM Do YYYY h:mmA');
    } else {
        formattedDate = dayjs(date).format('LLL');
    }

    return formattedDate;
}

function isEnglish() {
    const localStore = store.getState();
    const { country } = localStore.prefillReducer;
    let isEnglish = false;

    if (
        country === localizationConstants.AU ||
        country === localizationConstants.NZ ||
        country === localizationConstants.US ||
        localStore.prefillReducer.locale.toLowerCase() === 'en-ca'
    ) {
        isEnglish = true;
    }

    return isEnglish;
}

export function formatTime(date) {
    let formattedTime = '';

    if (isEnglish()) {
        formattedTime = dayjs(date).format('h:mm');
    } else {
        formattedTime = dayjs(date).format('H:mm');
    }

    return formattedTime;
}

// Normalizes the dealership timezone value coming from the appointment payload
// into a valid IANA timezone name (e.g. "America/New_York" or "UTC"), or null
// when it is missing or not a recognized timezone.
function normalizeTimeZoneName(dealershipTimeZone) {
    let tzName = dealershipTimeZone;

    if (tzName && typeof tzName === 'object') {
        tzName = tzName.displayName;
    }

    if (typeof tzName !== 'string') {
        return null;
    }

    tzName = tzName.trim();

    if (!tzName) {
        return null;
    }

    // Validate against the runtime's timezone database; invalid names throw.
    try {
        Intl.DateTimeFormat('en-US', { timeZone: tzName });
    } catch (e) {
        return null;
    }

    return tzName;
}

// Determines whether an appointment's scheduled date and time are in the past.
// The appointment wall-clock time is interpreted in the dealership timezone when
// available, so a same-day appointment whose time has already passed is treated
// as past. Falls back to the browser's local time when no timezone is provided.
export function isAppointmentInPast(appointmentDate, appointmentStart, dealershipTimeZone) {
    if (!appointmentDate || !appointmentStart) {
        return false;
    }

    // Normalize the time to HH:mm:ss so HH:mm, HH:mm:ss and HH:mm:ss.SSS all parse.
    const [hours = '00', minutes = '00', seconds = '00'] = String(appointmentStart).split(':');
    const timePart = `${hours}:${minutes}:${String(seconds).slice(0, 2)}`;
    const dateTime = `${appointmentDate} ${timePart}`;
    const format = 'YYYY-MM-DD HH:mm:ss';
    const tzName = normalizeTimeZoneName(dealershipTimeZone);

    let appointmentMoment;
    let now;

    if (tzName) {
        appointmentMoment = dayjs.tz(dateTime, format, tzName);
        now = dayjs();
    } else {
        appointmentMoment = dayjs(dateTime, format);
        now = dayjs();
    }

    if (!appointmentMoment.isValid()) {
        return false;
    }

    return appointmentMoment.isBefore(now);
}
